import { create } from "zustand";
import { persist } from "zustand/middleware";

interface FilterStore {
  filters: Record<string, any>;
  defaultValues: Record<string, any>;

  setFilters: <T>(key: string, newFilters: Partial<T>) => void;
  getFilters: <T>(key: string) => T;
  removeFilterKey: (key: string) => void;
  addItemsToFilter: <T>(key: string, items: Partial<T>) => void;
  removeAllFilters: () => void;
  removeItemFromFilter: (key: string, itemKey: string) => void;
  setDefaultValue: <T>(key: string, value: T) => void;
}

export const useFilterStore = create<FilterStore>()(
  persist(
    (set, get) => ({
      filters: {},
      defaultValues: {},

      setFilters: (key, newFilters) =>
        set((state) => ({
          filters: {
            ...state.filters,
            [key]: { ...(state.filters[key] || {}), ...newFilters },
          },
        })),

      getFilters: (key) => {
        const filters = get().filters[key];
        return filters !== undefined ? filters : (get().defaultValues[key] ?? {});
      },

      removeFilterKey: (key) =>
        set((state) => {
          const { [key]: _, ...rest } = state.filters;
          const defaultValue = state.defaultValues[key];
          return {
            filters: defaultValue ? { ...rest, [key]: defaultValue } : rest,
          };
        }),

      addItemsToFilter: (key, items) =>
        set((state) => ({
          filters: {
            ...state.filters,
            [key]: { ...(state.filters[key] || {}), ...items },
          },
        })),

      removeAllFilters: () =>
        set((state) => {
          const filtersWithDefaults = Object.keys(state.defaultValues).reduce(
            (acc, key) => {
              acc[key] = state.defaultValues[key];
              return acc;
            },
            {} as Record<string, any>
          );
          return { filters: filtersWithDefaults };
        }),

      removeItemFromFilter: (key, itemKey) =>
        set((state) => {
          const existing = state.filters[key] || {};
          const { [itemKey]: _, ...rest } = existing;
          return {
            filters: {
              ...state.filters,
              [key]: rest,
            },
          };
        }),

      setDefaultValue: (key, value) =>
        set((state) => ({
          defaultValues: {
            ...state.defaultValues,
            [key]: value,
          },
        })),
    }),
    {
      name: "filter-storage", // key in localStorage
      partialize: (state) => ({ filters: state.filters }), // only persist filters, not defaultValues
    }
  )
);
